import React, { forwardRef, useImperativeHandle, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { FieldLabel, FieldHint, FieldErrors } from "../../fields/index.jsx";
import { SingleLine } from "../../singleline/index.jsx";
import { BaseDropdown, BASE_DROPDOWN_POSITION } from "../base.jsx";

import styles from "./inputdropdown.css";

import DropDownIcon from "../small-down.svg";

export const INPUTDROPDOWN_TYPE = {
  NORMAL: "normal",
  MINIMAL: "minimal"
};

/**
 * Use this component to show the Dropdown with input fields
 */

export const InputDropDown = forwardRef((props, ref) => {
  const {
    label,
    id,
    required,
    hint,
    errors,
    value,
    placeholder,
    className,
    selectedValue,
    listRenderer,
    onChange,
    onFocus,
    onBlur,
    onOpen,
    onClose,
    onKeyDown,
    labelLeftIcon,
    labelRightIcon,
    onEnter,
    showList = true,
    type = INPUTDROPDOWN_TYPE.MINIMAL
  } = props;
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        containerRef.current.focus();
        inputRef.current.focus();
      }
    };
  });
  return (
    <div
      className={`${styles.inputDropdown} ${className}`}
      onBlur={(e) => {
        onBlur(e);
      }}
      ref={containerRef}
    >
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
        />
      )}
      <SingleLine
        id={id}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onEnter={onEnter}
        placeholder={placeholder}
        styleType={type}
        onBlur={(val, e) => {
          e.stopPropagation();
        }}
        addonAfter={
          showList ? (
            <BaseDropdown
              targetRenderer={() => {
                return (
                  <button
                    className={`${styles.optionsButton} ${styles[type]}`}
                    // onBlur={(e) => e.stopPropagation()}
                  >
                    <span className="base">{selectedValue}</span>{" "}
                    <DropDownIcon />
                  </button>
                );
              }}
              listRenderer={listRenderer}
              position={BASE_DROPDOWN_POSITION.BOTTOM_RIGHT}
              onOpen={onOpen}
              onClose={onClose}
            />
          ) : (
            <button
              className={`${styles.optionsButton} ${styles[type]}`}
              style={{ pointerEvents: "none" }}
            >
              <span className="base">{selectedValue}</span>
            </button>
          )
        }
        ref={inputRef}
      />
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && <FieldErrors errors={errors} targetId={id} />}
    </div>
  );
});

InputDropDown.propTypes = {
  /**
   * Display label for the field
   */
  label: PropTypes.string,
  id: PropTypes.string,
  /**
   * To override the default styles
   */
  className: PropTypes.string,
  /**
   * To make as mandatory fields
   */
  required: PropTypes.bool,
  /**
   * Info about the fields can be shown using hint prop
   */
  hint: PropTypes.string,
  /**
   * Error in the fields can be passed inside errors prop
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Value of the input fields
   */
  value: PropTypes.string.isRequired,
  /**
   * Placeholder of the input fields
   */
  placeholder: PropTypes.string,
  /**
   * Selected value in the dropdown
   */
  selectedValue: PropTypes.string,
  /**
   * User can customise the dropdown list via this prop
   */
  listRenderer: PropTypes.func,
  /**
   * Trigger when user type some value in input
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Trigger when user focus on the input
   */
  onFocus: PropTypes.func,
  /**
   * Trigger when user leaves the input
   */
  onBlur: PropTypes.func,
  /**
   * Trigger when dropdown opens
   */
  onOpen: PropTypes.func,
  /**
   * Trigger when dropdown close
   */
  onClose: PropTypes.func,
  /**
   * Trigger when keyboard down key is pressed
   */
  onKeyDown: PropTypes.func,
  /**
   * Trigger when user press enter button
   */
  onEnter: PropTypes.func,
  /**
   * Prop to disable dropdown
   */
  showList: PropTypes.bool,
  /**
   * Type of input dropdowm
   */
  type: PropTypes.oneOf(Object.values(INPUTDROPDOWN_TYPE)),
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.string,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

InputDropDown.defaultProps = {
  showList: true,
  type: INPUTDROPDOWN_TYPE.MINIMAL
};
